import { supabase } from '@shared/lib/supabase/client'
import type { Database } from '@shared/types/database'

type Thema = Database['public']['Tables']['themas']['Row']

export interface ThemaHierarchyWithCounts {
  id: string
  naam: string
  totalCount: number
  children: {
    id: string
    naam: string
    count: number
  }[]
}

class ThemasService {
  async getAll(): Promise<Thema[]> {
    const { data, error } = await supabase
      .from('themas')
      .select('*')
      .order('naam')

    if (error) {
      console.error('Error fetching themas:', error)
      return []
    }

    return data || []
  }

  async getById(id: string): Promise<Thema | null> {
    const { data, error } = await supabase
      .from('themas')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      console.error('Error fetching thema:', error)
      return null
    }

    return data
  }

  async getWithCaseCount(filters?: {
    searchQuery?: string
    rechtsgebiedIds?: string[]
    instantieIds?: string[]
    zaakTypeIds?: string[]
    dateFrom?: string
    dateTo?: string
    portalId?: string
  }): Promise<(Thema & { case_count: number; jurisprudentie_ids: string[] })[]> {
    // Warn if portalId is provided - this method doesn't support portal filtering
    // Use getHierarchyWithCounts instead for portal-aware thema counts
    if (filters?.portalId) {
      console.warn(
        'themasService.getWithCaseCount: portalId filter is not supported. ' +
        'Use getHierarchyWithCounts for portal-aware thema counts.'
      )
    }

    // Build the query for counting cases per thema
    let query = supabase
      .from('jurisprudentie_themas')
      .select(`
        thema_id,
        jurisprudentie_id,
        jurisprudentie:jurisprudentie!inner(
          id,
          ecli,
          titel,
          publicatie_datum,
          uitspraak_datum,
          instantie_id,
          zoek_vector
        )
      `)

    // Apply filters to jurisprudentie
    if (filters?.rechtsgebiedIds && filters.rechtsgebiedIds.length > 0) {
      // First fetch the jurisprudentie IDs that match the rechtsgebied filter
      const { data: rechtsgebiedJurisprudentie } = await supabase
        .from('jurisprudentie_rechtsgebieden')
        .select('jurisprudentie_id')
        .in('rechtsgebied_id', filters.rechtsgebiedIds)

      const jurisprudentieIds = rechtsgebiedJurisprudentie?.map(r => r.jurisprudentie_id) || []

      if (jurisprudentieIds.length > 0) {
        query = query.in('jurisprudentie.id', jurisprudentieIds)
      } else {
        // No matching cases, return empty
        return []
      }
    }

    if (filters?.instantieIds && filters.instantieIds.length > 0) {
      query = query.in('jurisprudentie.instantie_id', filters.instantieIds)
    }

    if (filters?.zaakTypeIds && filters.zaakTypeIds.length > 0) {
      // Note: This would require a zaak_type_id field in jurisprudentie table
      // Skipping for now if not available
    }

    if (filters?.dateFrom) {
      query = query.gte('jurisprudentie.uitspraak_datum', filters.dateFrom)
    }

    if (filters?.dateTo) {
      query = query.lte('jurisprudentie.uitspraak_datum', filters.dateTo)
    }

    // Apply search query if provided
    if (filters?.searchQuery) {
      const searchTerms = filters.searchQuery.trim().split(/\s+/).filter(term => term.length > 0)
      if (searchTerms.length > 0) {
        const tsQuery = searchTerms.map(term => `${term}:*`).join(' & ')
        query = query.textSearch('jurisprudentie.zoek_vector', tsQuery, {
          type: 'websearch',
          config: 'dutch'
        })
      }
    }

    const { data: junctionData, error: junctionError } = await query

    if (junctionError) {
      console.error('Error fetching thema counts:', junctionError)
      return []
    }

    // Count cases per thema and track jurisprudentie IDs
    const themaCounts = new Map<string, Set<string>>()
    junctionData?.forEach((item: any) => {
      if (!themaCounts.has(item.thema_id)) {
        themaCounts.set(item.thema_id, new Set())
      }
      themaCounts.get(item.thema_id)!.add(item.jurisprudentie_id)
    })

    // Get all themas
    const allThemas = await this.getAll()

    // Combine themas with counts
    return allThemas.map(thema => {
      const jurisprudentieIds = themaCounts.get(thema.id) || new Set()
      return {
        ...thema,
        case_count: jurisprudentieIds.size,
        jurisprudentie_ids: Array.from(jurisprudentieIds)
      }
    })
  }

  async getHierarchyWithCounts(filters?: {
    searchQuery?: string
    rechtsgebiedIds?: string[]
    instantieIds?: string[]
    zaakTypeIds?: string[]
    wetsartikelIds?: string[]
    dateFrom?: string
    dateTo?: string
    portalIds?: string[]
  }): Promise<ThemaHierarchyWithCounts[]> {
    try {
      // Use provided portal IDs or empty array (database will use default)
      const portalIds = filters?.portalIds || [];

      // Use the new database function for better performance
      const { data, error } = await (supabase as any).rpc('get_thema_counts', {
        zoekterm: filters?.searchQuery || null,
        instantie_ids: filters?.instantieIds?.length ? filters.instantieIds : null,
        rechtsgebied_ids: filters?.rechtsgebiedIds?.length ? filters.rechtsgebiedIds : null,
        van_datum: filters?.dateFrom || null,
        tot_datum: filters?.dateTo || null,
        zaak_type_ids: filters?.zaakTypeIds?.length ? filters.zaakTypeIds : null,
        wetsartikel_ids: filters?.wetsartikelIds?.length ? filters.wetsartikelIds : null,
        p_portals: portalIds
      })

      if (error) {
        console.error('Error fetching thema counts:', error)
        return []
      }

      // Transform the flat data into hierarchy
      const themasWithCounts = (data || []) as Array<{
        thema_id: string
        thema_naam: string
        parent_id: string | null
        item_case_count: number
      }>

      // Build hierarchy
      const hierarchyMap = new Map<string, ThemaHierarchyWithCounts>()

      // Group themas by parent
      const childrenByParent = new Map<string, typeof themasWithCounts>()

      themasWithCounts.forEach(thema => {
        if (thema.parent_id) {
          if (!childrenByParent.has(thema.parent_id)) {
            childrenByParent.set(thema.parent_id, [])
          }
          childrenByParent.get(thema.parent_id)!.push(thema)
        }
      })

      // Build hierarchy with correct counts
      themasWithCounts.forEach(thema => {
        if (thema.parent_id) {
          // This is a child - will be added to parent later
          return
        }

        // This is a parent or root thema
        const children = childrenByParent.get(thema.thema_id) || []

        // Calculate total count: sum of parent count and all children counts
        const totalCount = Number(thema.item_case_count) +
          children.reduce((sum, child) => sum + Number(child.item_case_count), 0)

        hierarchyMap.set(thema.thema_id, {
          id: thema.thema_id,
          naam: thema.thema_naam,
          totalCount,
          children: children.map(child => ({
            id: child.thema_id,
            naam: child.thema_naam,
            count: Number(child.item_case_count)
          })).sort((a, b) => b.count - a.count)
        })
      })

      // Convert to array and sort
      return Array.from(hierarchyMap.values())
        .filter(category => category.totalCount > 0)
        .sort((a, b) => b.totalCount - a.totalCount)
    } catch (error) {
      console.error('Error in getHierarchyWithCounts:', error)
      return []
    }
  }
}

export const themasService = new ThemasService()
